/**
 * AppShell Layout
 * Main application layout with navigation and content area
 */

import { ReactNode, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
  rectIntersection,
  pointerWithin,
  type CollisionDetection,
} from '@dnd-kit/core';
import { useDragSensors, dropAnimationConfig } from '../../hooks';
import { useKeyboardShortcuts } from '../../hooks';
import { useUIStore } from '../../stores/ui';
import { useSetOpsStore } from '../../stores/setops';
import { useSequencerStore } from '../../stores/sequencer';
import { useWorkbenchStore } from '../../stores/workbench';
import { useSelectionStore } from '../../stores/selection';
import { useAddTracks } from '../../api/hooks';
import type { TrackWithInstanceId } from '../../api/types';
import { Header } from './Header';
import { PlayerBar } from './PlayerBar';
import { LibrarySidebar } from './LibrarySidebar';
import { DiscoveryPanel } from './DiscoveryPanel';
import { TrackRow } from '../workbench/TrackRow';
import { HistoryView } from '../history/HistoryView';
import { SequencerView } from '../sequencer/SequencerView';
import { toast } from '../ui/Toast';

interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  // Initialize keyboard shortcuts
  useKeyboardShortcuts();

  // Get current mode from UI store
  const { mode } = useUIStore();

  // Drag state
  const [activeTrack, setActiveTrack] = useState<TrackWithInstanceId | null>(null);
  const [draggingTrackCount, setDraggingTrackCount] = useState<number>(0);

  // SetOps drag-and-drop state
  const { isDragging, draggedPlaylist, endDrag, resultTracks } = useSetOpsStore();

  // Sequencer state
  const { isOpen: isSequencerOpen, updateSlot, reorderSlots } = useSequencerStore();

  // Workbench state
  const { tracks, reorderTracks, addTrack } = useWorkbenchStore();

  // Selection state
  const { isSelected, getSelectedIds } = useSelectionStore();

  // API hook for adding tracks to playlists
  const addTracks = useAddTracks();

  // Sensors for drag interactions
  const sensors = useDragSensors();

  // Custom collision detection - use pointerWithin for better drop zone detection
  const customCollisionDetection: CollisionDetection = (args) => {
    const activeData = args.active.data.current;

    // For external tracks (from discovery panel), use pointerWithin to find track rows
    if (activeData?.['type'] === 'external-track') {
      const pointerCollisions = pointerWithin(args);

      // Prioritize track-drop-zones (individual track rows)
      const trackDropZones = pointerCollisions.filter(
        (collision) => collision.id.toString().startsWith('track-drop-')
      );

      if (trackDropZones.length > 0) {
        return trackDropZones;
      }

      return pointerCollisions;
    }

    // For playlists (from library sidebar), use pointerWithin to find drop zones
    if (activeData?.['type'] === 'playlist') {
      const pointerCollisions = pointerWithin(args);

      // Prioritize track-drop-zones (for inserting playlist tracks between workbench tracks)
      const trackDropZones = pointerCollisions.filter(
        (collision) => collision.id.toString().startsWith('track-drop-')
      );

      if (trackDropZones.length > 0) {
        return trackDropZones;
      }

      // Otherwise return all collisions (includes header, sequencer slots)
      return pointerCollisions;
    }

    // For sortable tracks (internal reordering), check for playlist drop zones first
    if (activeData?.['sortable']) {
      const pointerCollisions = pointerWithin(args);

      // Check for playlist-drop-zones (playlists in sidebar)
      const playlistDropZones = pointerCollisions.filter(
        (collision) => collision.id.toString().startsWith('playlist-drop-')
      );

      if (playlistDropZones.length > 0) {
        return playlistDropZones;
      }

      // Check for track-drop-zones (between tracks in workbench)
      const trackDropZones = pointerCollisions.filter(
        (collision) => collision.id.toString().startsWith('track-drop-')
      );

      if (trackDropZones.length > 0) {
        return trackDropZones;
      }

      // Otherwise use rect intersection for sortable reordering
      return rectIntersection(args);
    }

    // Default: rect intersection
    return rectIntersection(args);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const activeData = active.data.current;

    // Check if dragging a track from workbench (sortable)
    if (activeData?.['sortable']) {
      const track = tracks.find((t) => t.instanceId === active.id);
      setActiveTrack(track ?? null);

      // Check if this track is part of a selection
      if (track && isSelected(track.instanceId)) {
        // Include all selected tracks in the drag
        const selectedIds = getSelectedIds();
        setDraggingTrackCount(selectedIds.length);
      } else {
        // Single track drag
        setDraggingTrackCount(1);
      }
    }
    // Check if dragging a track from discovery panel (external-track)
    else if (activeData?.['type'] === 'external-track') {
      setActiveTrack(activeData['track'] as TrackWithInstanceId);
      setDraggingTrackCount(1);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    // Reset active track
    setActiveTrack(null);
    setDraggingTrackCount(0);

    if (!over) {
      endDrag();
      return;
    }

    const activeData = active.data.current;
    const overData = over.data.current;

    // Case 1: Track reordering within workbench (sortable)
    if (activeData?.['sortable'] && overData?.['sortable']) {
      const oldIndex = tracks.findIndex((t) => t.instanceId === active.id);
      const newIndex = tracks.findIndex((t) => t.instanceId === over.id);

      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        reorderTracks(oldIndex, newIndex);
      }
      return;
    }

    // Case 2: Track/Playlist insertion to specific track position
    if (overData?.['type'] === 'track-drop-zone') {
      const position = overData['position'] as number;

      // Case 2a: Single track from discovery panel
      if (activeData?.['type'] === 'external-track') {
        const track = activeData['track'] as TrackWithInstanceId;
        addTrack(track, position);
        endDrag();
        return;
      }

      // Case 2b: Playlist from library - add all tracks at position
      if (activeData?.['type'] === 'playlist') {
        const playlist = activeData['playlist'];
        // Fetch playlist tracks and add them
        import('../../api/client')
          .then(({ getApiClient }) => {
            return getApiClient().get(`/playlists/${playlist.id}`);
          })
          .then((playlistDetail: any) => {
            const playlistTracks = playlistDetail.tracks.items.map((pt: any) => pt.track);
            useWorkbenchStore.getState().addTracks(playlistTracks, position);
          })
          .catch((error) => {
            console.error('Failed to fetch playlist tracks:', error);
            toast.error('Failed to load playlist tracks');
          });
        endDrag();
        return;
      }
    }

    // Case 3: Sequencer slot reordering (slot → slot)
    if (activeData?.['type'] === 'sequencer-slot' && overData?.['type'] === 'sequencer-slot') {
      reorderSlots(active.id as string, over.id as string);
      return;
    }

    // Case 4: Playlist drop to sequencer slot (library → slot)
    if (activeData?.['playlist'] && overData?.['type'] === 'sequencer-slot') {
      updateSlot(overData['slotId'], activeData['playlist']);
      endDrag();
      return;
    }

    // Case 5: Track(s) drop to playlist (workbench → playlist in sidebar)
    if (activeData?.['sortable'] && overData?.['type'] === 'playlist-drop-zone') {
      const playlistId = overData['playlistId'] as string;
      const draggedTrack = tracks.find((t) => t.instanceId === active.id);

      if (!draggedTrack) {
        endDrag();
        return;
      }

      // Determine which tracks to add
      let tracksToAdd: TrackWithInstanceId[];
      if (isSelected(draggedTrack.instanceId)) {
        // Multi-select: Add all selected tracks
        const selectedIds = getSelectedIds();
        tracksToAdd = tracks.filter((t) => selectedIds.includes(t.instanceId));
      } else {
        // Single track: Add just this track
        tracksToAdd = [draggedTrack];
      }

      // Extract URIs
      const uris = tracksToAdd.map((t) => t.uri);

      // Add tracks to playlist (at end, position: undefined)
      addTracks.mutate(
        { playlistId, data: { uris } },
        {
          onSuccess: () => {
            const count = tracksToAdd.length;
            toast.success(`Added ${count} track${count !== 1 ? 's' : ''} to playlist`);
          },
          onError: (error: any) => {
            console.error('Failed to add tracks to playlist:', error);
            toast.error('Failed to add tracks to playlist');
          },
        }
      );

      endDrag();
      return;
    }

    // Case 6: SetOps drop (library → workbench header)
    // When playlist is dropped on header, show the operation selector
    // Don't call endDrag() here - it will be called when selector closes
    if (activeData?.['type'] === 'playlist' && over.id === 'workbench-header-setops') {
      // Don't show selector if sequencer is open or there are unsaved results
      const hasUnsavedResult = resultTracks && resultTracks.length > 0;
      if (!isSequencerOpen && !hasUnsavedResult) {
        useSetOpsStore.getState().showOperationSelector();
        return;
      }
      // Otherwise just end the drag
    }

    endDrag();
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={customCollisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="h-screen w-screen flex flex-col bg-zinc-950 text-zinc-100 overflow-hidden">
        <Header />

        <main className="flex-1 flex overflow-hidden">
          <LibrarySidebar />

          {/* Main Content Area */}
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
            {mode === 'workbench' ? children : mode === 'history' ? <HistoryView /> : <SequencerView />}
          </div>

          {/* Discovery Panel - Only visible in Workbench mode */}
          {mode === 'workbench' && <DiscoveryPanel />}
        </main>

        <PlayerBar />
      </div>

      {/* DragOverlay shows preview of dragged item */}
      <DragOverlay dropAnimation={dropAnimationConfig} zIndex={9999}>
        {/* Dragged playlist */}
        {isDragging && draggedPlaylist ? (
          <div className="bg-zinc-800 rounded-lg shadow-2xl p-3 border border-zinc-600 opacity-90 pointer-events-none">
            <div className="flex items-center gap-3">
              {draggedPlaylist.images?.[0]?.url && (
                <img
                  src={draggedPlaylist.images[0].url}
                  alt={draggedPlaylist.name}
                  className="w-12 h-12 rounded object-cover"
                />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-zinc-100 truncate">
                  {draggedPlaylist.name}
                </p>
                <p className="text-xs text-zinc-500">
                  {draggedPlaylist.tracksCount} tracks
                </p>
              </div>
            </div>
          </div>
        ) : null}

        {/* Dragged track */}
        {activeTrack ? (
          <div className="bg-zinc-900 rounded-md shadow-2xl border border-zinc-700 opacity-90 pointer-events-none">
            <TrackRow
              track={activeTrack}
              position={tracks.indexOf(activeTrack) + 1}
              audioFeatures={undefined}
              allTrackIds={tracks.map((t) => t.instanceId)}
              isDragging
            />
            {draggingTrackCount > 1 && (
              <div className="absolute top-2 right-2 bg-emerald-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">
                {draggingTrackCount} tracks
              </div>
            )}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
